/**
 * Campo de preço da revisão do import (#474 revisões UX). Escrever não
 * reformata; no blur o texto é convertido em cêntimos e o campo reformatado.
 *
 * FORMATO E PARSE SÃO SIMÉTRICOS pela moeda (UX r3, bloqueante 1): o parse
 * aceita como DECIMAL um separador seguido de até N dígitos, N = casas da
 * moeda (KWD «8,000»/«8.000» → 8,000 KWD = 800 price_cents; JPY não tem
 * parte decimal) — format(parse(x)) === x para qualquer moeda/locale. Aceita
 * agrupamentos «1’234.50» (de-CH), «1 234,50» (espaços), «1.234,50»,
 * «1,234.50». Formata no IDIOMA DO PAINEL (useI18n lang), não o do browser.
 * Focar e sair sem escrever não faz commit. Negativo/vazio/ilegível é
 * INVÁLIDO: o que o dono escreveu FICA no campo (aria-invalid + mensagem
 * ligada por aria-describedby) e o valor gravado não muda.
 *
 * QA #474 r3 (item 2c): `readOnly` para o prato COM variantes — o valor é o
 * base DERIVADO (mínimo das opções com preço) e não é editável; `hint`
 * explica porquê (texto pequeno ligado por aria-describedby).
 */
import { useState } from 'react'
import { useI18n } from '../lib/i18n.js'

/** Decimais da moeda para o campo/chip (JPY 0, KWD/BHD/OMR/JOD/TND 3,
 *  resto 2) — mesmo critério do priceLabel do core, sem o símbolo. */
export function currencyFractionDigits(currency?: string | null): number {
  if (!currency) return 2
  try {
    const digits = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits
    return typeof digits === 'number' && digits >= 0 && digits <= 20 ? digits : 2
  } catch {
    return 2
  }
}

/** Cêntimos → texto, com os decimais da moeda e o idioma do painel. */
export function formatPriceCents(cents: number, currency?: string | null, locale?: string): string {
  const digits = currencyFractionDigits(currency)
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(cents / 100)
  } catch {
    return (cents / 100).toFixed(digits)
  }
}

/** Agrupadores que NÃO são decimais em nenhum locale — removidos sempre. */
const GROUPING_NOISE = /[\u2019'\u00A0\u202F\u2009 ]/g

/**
 * Texto do campo → cêntimos, ou null quando é inválido (não muda o valor).
 * `currency` define N = casas decimais aceites: um ÚNICO separador seguido
 * de 1..N dígitos é decimal («8,000» em KWD → 800; «12,50» em EUR → 1250);
 * caso contrário os separadores são de milhar, em grupos de 3 («1.234»,
 * «1,234,567»); «12,5,6» é inválido.
 */
export function parsePriceInput(text: string, currency?: string | null): number | null {
  const cleaned = text.trim().replace(GROUPING_NOISE, '')
  if (!/^\d[.,\d]*$/.test(cleaned)) return null // vazio, sinal, letras, Infinity…
  const digits = currencyFractionDigits(currency)
  const sepIdx = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','))
  if (sepIdx === -1) return wholeToCents(cleaned)
  const decimalSep = cleaned[sepIdx]!
  const frac = cleaned.slice(sepIdx + 1)
  const isDecimal =
    cleaned.indexOf(decimalSep) === sepIdx && frac.length >= 1 && frac.length <= digits
  if (isDecimal) {
    const whole = cleaned
      .slice(0, sepIdx)
      .split(decimalSep === '.' ? ',' : '.')
      .join('')
    if (!/^\d{1,3}(\d{3})*$/.test(whole)) return null
    return wholeToCents(`${whole}.${frac ?? ''}`)
  }
  // todos os separadores são de milhar: grupos de 3 (a cauda também)
  const groups = cleaned.split(/[.,]/)
  if (!/^\d{1,3}$/.test(groups[0]!) || !groups.slice(1).every((g) => /^\d{3}$/.test(g))) {
    return null
  }
  return wholeToCents(groups.join(''))
}

function wholeToCents(units: string): number | null {
  const n = Number(units)
  if (!Number.isFinite(n)) return null
  const cents = Math.round(n * 100)
  return Number.isSafeInteger(cents) ? cents : null
}

export function ImportReviewPriceInput({
  cents,
  onCommit,
  label,
  describedBy,
  testId,
  small = false,
  currency,
  readOnly = false,
  hint,
}: {
  cents: number
  onCommit: (cents: number) => void
  /** aria-label («Preço de {name}») — o campo não tem <label> visível */
  label: string
  /** id do selo/hint para aria-describedby */
  describedBy?: string | undefined
  testId?: string
  /** versão pequena para dentro do chip da variante */
  small?: boolean
  /** moeda do import: define os decimais do display e do parse */
  currency?: string | null | undefined
  /** QA r3 (item 2c): campo SÓ DE LEITURA — valor derivado, nunca comita */
  readOnly?: boolean
  /** explicação do modo só de leitura (base derivado das variantes) */
  hint?: string | undefined
}) {
  const { t, lang } = useI18n()
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [invalid, setInvalid] = useState(false)
  // UX r3 ponto 1: o que estava no campo ao FOCAR — blur sem edição não comita
  const [focusText, setFocusText] = useState('')
  const display = invalid || editing ? text : formatPriceCents(cents, currency, lang)
  const msgId = `${testId ?? 'import-review-price'}-invalid-msg`
  const hintId = `${testId ?? 'import-review-price'}-derived-hint`
  const described = [describedBy, hint ? hintId : undefined, ...(invalid ? [msgId] : [])]
    .filter(Boolean)
    .join(' ')
  return (
    <span className="inline-flex flex-col items-end">
      <input
        data-testid={testId}
        value={display}
        aria-label={label}
        aria-invalid={invalid || undefined}
        {...(described ? { 'aria-describedby': described } : {})}
        {...(readOnly
          ? { readOnly: true }
          : {
              onFocus: () => {
                const formatted = formatPriceCents(cents, currency, lang)
                setText(formatted)
                setFocusText(formatted)
                setInvalid(false)
                setEditing(true)
              },
              onChange: (e) => {
                setText(e.target.value)
                setInvalid(false)
              },
              onBlur: (e) => {
                setEditing(false)
                // UX r3 ponto 1: focus + Tab sem escrever → nada muda
                if (e.target.value === focusText) return
                // UX r3 ponto 7: o DOM é a fonte do que o dono escreveu
                const parsed = parsePriceInput((e.target as HTMLInputElement).value, currency)
                if (parsed === null) {
                  // inválido: o TEXTO FICA no campo e o valor gravado não muda
                  setInvalid(true)
                  return
                }
                onCommit(parsed)
              },
            })}
        inputMode="decimal"
        className={`w-20 text-sm text-end rounded border px-2 min-h-11 ${
          invalid ? 'border-red-400' : 'border-gray-200'
        } ${readOnly ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''} ${
          small ? 'py-0.5' : 'py-1'
        }`}
      />
      {hint && (
        <span
          id={hintId}
          data-testid="import-review-price-derived-hint"
          className={`text-[10px] text-gray-500 ${small ? '' : 'self-end'}`}
        >
          {hint}
        </span>
      )}
      {invalid && (
        <span
          id={msgId}
          data-testid="import-review-price-invalid"
          role="alert"
          className={`text-[10px] text-red-600 ${small ? '' : 'self-end'}`}
        >
          {t('admin.import.price_invalid')}
        </span>
      )}
    </span>
  )
}
